import { get, isNil, toUpper } from 'lodash';
import PropTypes from 'prop-types';
import '@/Components/DeviceInventory/DeviceInventory.less';
import {
  NewHC200Icon,
  PairedIcon,
  BatteryLowIcon,
  BatteryHighIcon,
  BatteryMediumIcon,
  ZeroLightIcon,
  LowLightIcon,
  MediumLightIcon,
  HighLightIcon,
  UnpairedIcon,
  BatteryZeroIcon,
  RCBatteryHighIcon,
  RCBatteryLowIcon,
  RCBatteryMediumIcon,
  RCBatteryZeroIcon,
  BloodPressureIcon,
  BodyWeightIcon,
  BodyTemperatureIcon,
  PulseOxygenIcon,
  FallbackMedicalIcon,
  GlucoseMeterIcon,
} from '@/Components/Common/Icons';
import environmentalRanges from '../../../environmentalRanges.json';
import { devicesInventory } from '@/utilities/data';
import ToiletFlushSensorIcon from '@/Components/Common/Icons/toilet-flush-sensor.svg';
import DoorSensorIcon from '@/Components/Common/Icons/door-sensor-icon.svg';
import SmartPlugIcon from '@/Components/Common/Icons/smart-plug.svg';
import OccupancySensorIcon from '@/Components/Common/Icons/occupancy-sensor.svg';
import IncontinenceSensorIcon from '@/Components/Common/Icons/incontinence-icon.svg';
import RadarSensorIcon from '@/Components/Common/Icons/radar-sensor.svg';
import CooktopSensorIcon from '@/Components/Common/Icons/cook-top-sensor.svg';
import {
  getStatusBatteryClassNames,
  getStatusClassNames,
  getSensorVariant,
} from '@/utilities/helpers/commonHelper';
import {
  DEVICE_PRODUCT_CLASS,
  SENSORS,
  SENSOR_VARIANTS,
  DEPLOYMENT_STATES,
} from '@/constants/device';

const { device_types } = devicesInventory;

const componentMap = {
  BatteryLowIcon,
  BatteryMediumIcon,
  BatteryHighIcon,
  ZeroLightIcon,
  LowLightIcon,
  MediumLightIcon,
  HighLightIcon,
  RCBatteryHighIcon,
  RCBatteryLowIcon,
  RCBatteryMediumIcon,
  RCBatteryZeroIcon,
  BloodPressureIcon,
  BodyWeightIcon,
  BodyTemperatureIcon,
  PulseOxygenIcon,
  FallbackMedicalIcon,
  GlucoseMeterIcon,
};

const getIconComponent = (levels, value) => {
  for (const level of levels) {
    if (
      (level.max !== undefined && value <= level.max) ||
      (level.min !== undefined && value >= level.min && level.max === undefined)
    ) {
      return componentMap[level.component];
    }
  }
  return null;
};

const getMedicalDeviceTitle = (device, trans) => {
  if (!device?.devicetype) {
    return device?.devicetype;
  }
  const newTitle = device_types['device_title'][device.devicetype];
  let response = '';
  if (newTitle) {
    response = newTitle;
  } else {
    response = device['devicetype']
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
  }

  return trans(response.replace(' ', '').toUpperCase());
};

export const getTranslatedLocationName = (locationKey, userConfig, selectedLanguage) => {
  if (
    typeof locationKey !== 'string' ||
    locationKey.trim() === '' ||
    typeof selectedLanguage !== 'string' ||
    selectedLanguage.trim() === '' ||
    typeof userConfig !== 'object' ||
    userConfig === null ||
    Array.isArray(userConfig)
  ) {
    return false;
  }
  const languageMap = {
    en: 'location_names_en',
    es: 'location_names_es',
    fr: 'location_names_fr',
    nl: 'location_names_nl',
  };
  const locationSetup = userConfig?.homesight_fti?.em200_location_setup;
  if (!locationSetup) return locationKey;
  const languagekey = languageMap[selectedLanguage.split('-')[0]];
  const translations = locationSetup[languagekey];
  if (!translations) return locationKey;

  const newTranslation = JSON.parse(convertToValidJSON(translations));
  const newlocationkey = locationKey.replace(/\s+/g, '').toLowerCase();
  const translation = newTranslation.find((loc) => loc.index === newlocationkey);
  return translation ? translation.name : newlocationkey;
};

function convertToValidJSON(str) {
  let fixedStr = str.replace(/'/g, '"');
  fixedStr = fixedStr.replace(/(\b\w+\b):/g, '"$1":');
  fixedStr = fixedStr.replace(/,\s*}/g, '}').replace(/,\s*]/g, ']');
  return fixedStr;
}

const getDeviceLocationName = (device) => {
  if (
    [
      ...device_types.smart_camera_devices.models,
      ...device_types.remote_control_devices.models,
      ...device_types.cooktop_sensor_devices.models,
    ].includes(device?.modelnumber) ||
    DEVICE_PRODUCT_CLASS.SMART_HOME.includes(device?.productclass)
  ) {
    return device?.location;
  } else {
    return device?.device_location;
  }
};

export const DeviceInfo = ({
  device,
  isModalView = false,
  userConfig,
  selectedLanguage,
  trans,
}) => {
  const location = getDeviceLocationName(device);
  const translatedLocation = getTranslatedLocationName(location, userConfig, selectedLanguage);
  const sensorVariant = getSensorVariant(device);
  const isIncontinenceSensor =
    device?.devicetype === SENSORS.SMART_HOME.OCCUPANCY_SENSOR &&
    sensorVariant === SENSOR_VARIANTS.INCONTINENCE;
  const isDemoAccount = device?.deployment_state === DEPLOYMENT_STATES.NOT_DEPLOYED;

  return (
    <div className="device-info">
      <div className="device-name-row">
        {device_types.smart_camera_devices.models.includes(device?.modelnumber) ? (
          <div className="device-name">
            {trans('WELLNESSHUB')} {isModalView && translatedLocation && `(${translatedLocation})`}
          </div>
        ) : device_types.remote_control_devices.models.includes(device.modelnumber) ? (
          <div className="device-name">
            {trans('REMOTECONTROL')}{' '}
            {isModalView && translatedLocation && `(${translatedLocation})`}
          </div>
        ) : device_types.sensor_devices.models.includes(device?.modelnumber) ? (
          <div className="device-name">
            {trans('ENVIORNMENTSENSOR')}{' '}
            {isModalView && translatedLocation && `(${translatedLocation})`}
          </div>
        ) : device_types.cooktop_sensor_devices.models.includes(device?.modelnumber) ? (
          <div className="device-name">
            {trans('COOKTOP_SENSOR')}{' '}
            {isModalView && translatedLocation && `(${translatedLocation})`}
          </div>
        ) : !isNil(device?.devicetype) ? (
          <div className="device-name">
            {isIncontinenceSensor
              ? trans('INCONTINENCEPADSENSOR')
              : trans(toUpper(device?.devicetype))}
          </div>
        ) : (
          <div className="device-name">
            {' '}
            {['NA', 'N/A'].includes(device?.devicetype)
              ? trans('UNKNOWN')
              : getMedicalDeviceTitle(device, trans)}
          </div>
        )}
        {isDemoAccount && (
          <div className="device-inventory-demo-tag-container">
            <span className="device-inventory-demo-tag-text">{trans('DEMO')}</span>
          </div>
        )}
      </div>
      {!isModalView && translatedLocation && translatedLocation !== false && (
        <div className="device-location">{translatedLocation}</div>
      )}
      <div className="device-serial-number">{device?.device_serial_number}</div>
    </div>
  );
};

DeviceInfo.propTypes = {
  device: PropTypes.object,
  isModalView: PropTypes.bool,
  userConfig: PropTypes.object,
  selectedLanguage: PropTypes.string,
  trans: PropTypes.func.isRequired,
};

export const DeviceConnectionIconInfo = ({ device, deviceStatus, connectionStatus }) => {
  const connectionless = get(device, 'connectionless', false);
  const sensorVariant = getSensorVariant(device);
  const isIncontinenceSensor =
    device?.devicetype === SENSORS.SMART_HOME.OCCUPANCY_SENSOR &&
    sensorVariant === SENSOR_VARIANTS.INCONTINENCE;

  const battery = (batteryLevel) => {
    const batteryLevels = device_types.remote_control_devices.models.includes(device?.modelnumber)
      ? environmentalRanges.environmentalRanges.rcBatteryLevels
      : environmentalRanges.environmentalRanges.batteryLevels;
    const IconComponent = getIconComponent(batteryLevels, batteryLevel);
    return IconComponent ? (
      <IconComponent
        connectionStatus={device?.paired_status === 'UNPAIRED' ? false : connectionStatus}
        connectionless={connectionless}
      />
    ) : null;
  };

  const temperatureBattery = (batteryLevel) => {
    const batteryLevels = device_types.remote_control_devices.models.includes(device?.modelnumber)
      ? environmentalRanges.environmentalRanges.rcBatteryLevels
      : environmentalRanges.environmentalRanges.batteryLevels;
    const IconComponent = getIconComponent(batteryLevels, batteryLevel);
    return IconComponent ? (
      <IconComponent
        connectionStatus={
          device?.deviceconnectstatus === 'CONNECTED' ? device?.deviceconnectstatus : false
        }
        connectionless={connectionless}
      />
    ) : null;
  };

  const medicalDeviceTypeIconMap =
    devicesInventory?.device_types?.medical_devices?.medical_device_icons;

  const getMedicalDeviceTypeIcon = (devicetype) => {
    const iconKey = medicalDeviceTypeIconMap[devicetype] || medicalDeviceTypeIconMap['default'];
    return componentMap[iconKey] || FallbackMedicalIcon;
  };
  const MedicalDeviceTypeIcon = getMedicalDeviceTypeIcon(device?.devicetype);

  const getCooktopConnectionClass = () => {
    const status = device?.deviceconnectstatus;
    if (typeof status === 'string') {
      const upper = status.toUpperCase();
      if (upper === 'DISCONNECTED') return 'disconnected';
      if (upper === 'CONNECTED') return 'connected';
      return '';
    }
    return '';
  };

  return (
    <>
      {device_types.smart_camera_devices.models.includes(device?.modelnumber) ? (
        <NewHC200Icon className="device-status-icon" status={deviceStatus} />
      ) : device_types.remote_control_devices.models.includes(device.modelnumber) ? (
        device?.device_battery_level === null || device?.device_battery_level === undefined ? (
          <RCBatteryZeroIcon
            className="device-status-icon"
            connectionStatus={device?.paired_status === 'UNPAIRED' ? false : connectionStatus}
          />
        ) : (
          <div className="connection-icon">{battery(device?.device_battery_level)}</div>
        )
      ) : device_types.sensor_devices.models.includes(device?.modelnumber) ? (
        device?.device_battery_level === null || device?.device_battery_level === undefined ? (
          <BatteryZeroIcon
            className="device-status-icon"
            connectionStatus={device?.paired_status === 'UNPAIRED' ? false : connectionStatus}
            connectionless={connectionless}
          />
        ) : (
          <div className="connection-icon">{battery(device?.device_battery_level)}</div>
        )
      ) : device?.devicetype === SENSORS.SMART_HOME.DOOR_WINDOW_SENSOR ? (
        <div className={`door-sensor-icon-container ${connectionless ? 'connectionless' : ''}`}>
          <DoorSensorIcon className={getStatusBatteryClassNames(device)} />
        </div>
      ) : device?.devicetype === SENSORS.SMART_HOME.SMART_PLUG ? (
        <div className={`smart-plug-sensor-icon ${connectionless ? 'connectionless' : ''}`}>
          <SmartPlugIcon className={getStatusClassNames(device)} />
        </div>
      ) : device?.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR ? (
        <div className={`cooktop-sensor-icon ${connectionless ? 'connectionless' : ''}`}>
          <CooktopSensorIcon className={getCooktopConnectionClass(device)} />
        </div>
      ) : device?.devicetype === SENSORS.SMART_HOME.OCCUPANCY_SENSOR ? (
        <div className={`occupancy-sensor-icon ${connectionless ? 'connectionless' : ''}`}>
          {isIncontinenceSensor ? (
            <IncontinenceSensorIcon className={getStatusBatteryClassNames(device)} />
          ) : (
            <OccupancySensorIcon className={getStatusBatteryClassNames(device)} />
          )}
        </div>
      ) : device?.devicetype === SENSORS.SMART_HOME.RADAR_SENSOR ? (
        <div
          className={`sensor-icon-container radar-sensor-icon ${getStatusClassNames(device)} ${connectionless ? 'connectionless' : ''}`}
        >
          <RadarSensorIcon className={getStatusBatteryClassNames(device)} />
        </div>
      ) : device?.devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR ? (
        <div className={`toilet-flush-sensor-icon ${connectionless ? 'connectionless' : ''}`}>
          <ToiletFlushSensorIcon className={getStatusBatteryClassNames(device)} />
        </div>
      ) : device?.devicetype === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR ? (
        device?.device_battery_level === null || device?.device_battery_level === undefined ? (
          <BatteryZeroIcon
            className="device-status-icon"
            connectionStatus={
              device?.deviceconnectstatus === 'CONNECTED' ? device?.deviceconnectstatus : false
            }
            connectionless={connectionless}
          />
        ) : (
          <div className="connection-icon">{temperatureBattery(device?.device_battery_level)}</div>
        )
      ) : device?.devicetype ? (
        <MedicalDeviceTypeIcon className="device-status-icon" />
      ) : (
        <div className="fallback-icon" style={{ width: '63px', height: '60px' }} />
      )}
    </>
  );
};

DeviceConnectionIconInfo.propTypes = {
  device: PropTypes.object,
  deviceStatus: PropTypes.string,
  connectionStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

const formatStatusTime = (isoString, timezone, is24hoursformat, trans) => {
  const date = new Date(isoString);
  let response = '';

  const month = String(date.toLocaleString('en-US', { timeZone: timezone, month: 'short' }));
  const translatedMonth = trans(month.toUpperCase());
  const day = date.toLocaleString('en-US', { timeZone: timezone, day: 'numeric' });
  response = `${translatedMonth} ${day}, ${date.toLocaleString('en-US', {
    timeZone: timezone,
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: !is24hoursformat,
  })}`;

  return response;
};

export const StatusInfo = ({
  lastConnectTime,
  lastDisconnectTime,
  device,
  timezone,
  connectionStatus,
  showDuration = true,
  tooltipMode = false,
  loginUserTimeformat,
  trans,
  deviceStatus,
}) => {
  const is24hoursformat = loginUserTimeformat?.is24hoursformat;
  const getConnectionStatusName = () => {
    if (device_types.sensor_devices.models.includes(device?.modelnumber)) {
      return device?.device_connected ? trans('CONNECTED') : trans('DISCONNECTED');
    } else if (device_types.cooktop_sensor_devices.models.includes(device?.modelnumber)) {
      const status = device?.deviceconnectstatus;
      if (typeof status === 'string') {
        return status.toUpperCase() === 'CONNECTED' ? trans('CONNECTED') : trans('DISCONNECTED');
      }
      return '-';
    } else if (device_types.remote_control_devices.models.includes(device.modelnumber)) {
      return device?.device_connection_status ? trans('CONNECTED') : trans('DISCONNECTED');
    } else if (device_types.smart_camera_devices.models.includes(device?.modelnumber)) {
      return deviceStatus === 'connected' ? trans('CONNECTED') : trans('DISCONNECTED');
    }
    return connectionStatus || trans('DISCONNECTED');
  };

  const isConnected = getConnectionStatusName() == trans('CONNECTED');
  const statusTime = isConnected ? lastConnectTime : lastDisconnectTime;

  const calculateTimeDifference = (lastConnectTime) => {
    const currentTime = new Date();
    const lastConnectDate = new Date(lastConnectTime);
    const diffInMilliseconds = currentTime - lastConnectDate;
    const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const days = Math.floor(diffInHours / 24);
    const hours = diffInHours % 24;

    if (diffInSeconds < 60) return trans('1MINUTE');
    if (diffInMinutes < 60)
      return `${diffInMinutes} ${trans('MINUTE')}${diffInMinutes !== 1 ? trans('S_L') : ''}`;
    if (days > 0)
      return `${days} ${trans('DAY')}${days > 1 ? trans('S_L') : ''} ${
        hours > 0 ? `${hours} ${trans('HOUR')}${hours > 1 ? trans('S_L') : ''}` : ''
      }`;
    return `${hours} ${trans('HOUR')}${hours > 1 ? trans('S_L') : ''}`;
  };

  const renderBatteryLevel = (device) => {
    if (
      device?.devicetype === SENSORS.SMART_HOME.SMART_PLUG ||
      device?.devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR
    ) {
      return null;
    }

    return (
      <div className="device-battery-info-container">
        <span className="device-battery-level-label">{trans('BATTERYLEVEL:')}</span>
        <span className="device-battery-level">
          {device?.device_battery_level != null && device?.device_battery_level >= 0
            ? `${device?.device_battery_level}%`
            : '-'}
        </span>
      </div>
    );
  };

  const formattedLastUsedTime =
    device?.device_last_used_time &&
    !['NA', 'N/A'].includes(device?.device_last_used_time?.toUpperCase())
      ? formatStatusTime(device?.device_last_used_time, timezone, is24hoursformat, trans)
      : null;

  return (
    <>
      {device_types.smart_camera_devices.models.includes(device?.modelnumber) ? (
        <div className={tooltipMode ? 'device-page-tooltip-status-info' : 'status-info'}>
          <>
            <div className="device-status-label">
              {isConnected ? trans('ONLINE') : trans('OFFLINE')}
              <span className="device-status-time">
                {trans('SINCE_L')} {formatStatusTime(statusTime, timezone, is24hoursformat, trans)}
              </span>
            </div>
            {showDuration && (
              <div className="device-status-connection-time">
                {trans('FOR')} {calculateTimeDifference(statusTime)}
              </div>
            )}
          </>
        </div>
      ) : [
          ...device_types.sensor_devices.models,
          ...device_types.remote_control_devices.models,
          ...device_types.cooktop_sensor_devices.models,
        ].includes(device?.modelnumber) ? (
        <div className={tooltipMode ? 'device-page-tooltip-status-info' : 'sensor-status'}>
          <div className="status-content">
            <div className="device-status-label">
              {`${getConnectionStatusName()} `}
              {isConnected && (
                <span className="device-status-time">
                  {trans('SINCE_L')}{' '}
                  {formatStatusTime(statusTime, timezone, is24hoursformat, trans)}
                </span>
              )}
            </div>
            {showDuration && (
              <>
                {isConnected && (
                  <div className="device-status-connection-time">
                    {trans('FOR')} {calculateTimeDifference(statusTime)}
                  </div>
                )}
                {renderBatteryLevel(device)}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className={tooltipMode ? 'device-page-tooltip-status-info' : 'sensor-status'}>
          <div className="status-content">
            {formattedLastUsedTime && (
              <div className="device-status-label">
                {trans('LASTUSED')}
                <span className="device-status-time">{formattedLastUsedTime}</span>
              </div>
            )}
            {showDuration && <>{renderBatteryLevel(device)}</>}
          </div>
        </div>
      )}
    </>
  );
};

StatusInfo.propTypes = {
  lastConnectTime: PropTypes.string,
  lastDisconnectTime: PropTypes.string,
  device: PropTypes.object,
  timezone: PropTypes.string,
  connectionStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  showDuration: PropTypes.bool,
  tooltipMode: PropTypes.bool,
  loginUserTimeformat: PropTypes.shape({ is24hoursformat: PropTypes.bool }),
  trans: PropTypes.func.isRequired,
  deviceStatus: PropTypes.string,
};

// Schema-driven tiles below: thin composition wrappers used as `x-component`
// targets by the Device Inventory schema (@/schemas/deviceInventory).

export const DeviceInfoTile = DeviceInfo;

export const PairedStatusTile = ({ device, trans }) => (
  <div className="paired-status">
    {device?.paired_status === 'PAIRED' || device?.paired_status === null ? (
      <PairedIcon label={trans('PAIRED')} />
    ) : (
      <UnpairedIcon label={trans('UNPAIRED')} />
    )}
  </div>
);

PairedStatusTile.propTypes = {
  device: PropTypes.object,
  trans: PropTypes.func.isRequired,
};

export const DeviceListLeftTile = ({ device, userConfig, selectedLanguage, trans }) => (
  <div className="device-card-left-container">
    <DeviceInfo
      device={device}
      userConfig={userConfig}
      selectedLanguage={selectedLanguage}
      trans={trans}
    />
    <PairedStatusTile device={device} trans={trans} />
  </div>
);

DeviceListLeftTile.propTypes = {
  device: PropTypes.object,
  userConfig: PropTypes.object,
  selectedLanguage: PropTypes.string,
  trans: PropTypes.func.isRequired,
};

export const DeviceStatusInfoTile = StatusInfo;

export const DeviceConnectionIconTile = ({ device, deviceStatus, connectionStatus }) => (
  <div className="device-connection-icon-info">
    <DeviceConnectionIconInfo
      device={device}
      deviceStatus={deviceStatus}
      connectionStatus={connectionStatus}
    />
  </div>
);

DeviceConnectionIconTile.propTypes = {
  device: PropTypes.object,
  deviceStatus: PropTypes.string,
  connectionStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

export const DeviceOnlineStatusGroupTile = ({
  device,
  timezone,
  connectionStatus,
  lastConnectTime,
  lastDisconnectTime,
  loginUserTimeformat,
  trans,
  deviceStatus,
}) => (
  <div className="device-online-status">
    <StatusInfo
      lastConnectTime={lastConnectTime}
      lastDisconnectTime={lastDisconnectTime}
      device={device}
      timezone={timezone}
      connectionStatus={connectionStatus}
      loginUserTimeformat={loginUserTimeformat}
      trans={trans}
      deviceStatus={deviceStatus}
    />
    <DeviceConnectionIconInfo
      device={device}
      deviceStatus={deviceStatus}
      connectionStatus={connectionStatus}
    />
  </div>
);

DeviceOnlineStatusGroupTile.propTypes = {
  device: PropTypes.object,
  timezone: PropTypes.string,
  connectionStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  lastConnectTime: PropTypes.string,
  lastDisconnectTime: PropTypes.string,
  loginUserTimeformat: PropTypes.shape({ is24hoursformat: PropTypes.bool }),
  trans: PropTypes.func.isRequired,
  deviceStatus: PropTypes.string,
};
